// Web-push subscription management (client side).
import { api } from "./api";
import { env } from "./env";

export type PushStatus = {
  supported: boolean;
  permission: NotificationPermission | "unsupported";
  subscribed: boolean;
};

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function readySubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/** Current per-device status for the settings indicator. */
export async function getPushStatus(): Promise<PushStatus> {
  if (!pushSupported()) return { supported: false, permission: "unsupported", subscribed: false };
  const sub = await readySubscription();
  let subscribed = false;
  if (sub) {
    try {
      const res = await api.get<{ subscribed: boolean }>(
        `/push/status?endpoint=${encodeURIComponent(sub.endpoint)}`,
      );
      subscribed = res.subscribed;
    } catch {
      subscribed = false;
    }
  }
  return { supported: true, permission: Notification.permission, subscribed };
}

/**
 * Request permission (must be called from a click handler — requesting on load
 * fails silently on iOS) and register this device.
 */
export async function enablePush(): Promise<PushStatus> {
  if (!pushSupported()) throw new Error("Push isn't supported on this browser.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { supported: true, permission, subscribed: false };
  }

  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(env.VITE_VAPID_PUBLIC_KEY),
    }));

  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });

  return { supported: true, permission, subscribed: true };
}

export async function disablePush(): Promise<void> {
  const sub = await readySubscription();
  if (!sub) return;
  await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}

/**
 * On every launch: if permission is granted but the browser dropped the
 * subscription (common after reinstall), silently re-register. Makes the whole
 * class of subscription-loss problems self-healing.
 */
export async function resubscribeIfMissing(): Promise<void> {
  if (!pushSupported() || Notification.permission !== "granted") return;
  try {
    const sub = await readySubscription();
    if (sub) {
      // Refresh lastSeenAt / rebind to the current user.
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: sub.endpoint,
        keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
      });
      return;
    }
    await enablePush();
  } catch (err) {
    console.error("[push] resubscribe failed", err);
  }
}
